/**
 * useIdleTimeout — fires a callback after a configurable period of user
 * inactivity. Used by InvestorPortalLayout to auto-logout investor sessions
 * after 30 minutes idle.
 *
 * Activity is detected via a fixed set of DOM events on `window`. The timer
 * resets on each activity event and starts immediately on mount.
 */
import { useEffect, useRef, useCallback } from 'react';

const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
] as const;

// ============================================================================
// HOOK
// ============================================================================

export function useIdleTimeout(
  onTimeout: () => void,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Keep a stable ref to onTimeout so resetTimer's useCallback identity is
  // not invalidated every render when the caller passes an inline function.
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const resetTimer = useCallback(() => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => onTimeoutRef.current(), timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, resetTimer));
    resetTimer(); // start initial timer on mount

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
      }
    };
  }, [resetTimer]);
}
